/*
 * AppReducer
 *
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 */
import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { UpdatedTnCCheckResponse } from "types/Dashboard";
import { ToastMessage } from "types/Toast";
import { UserDetails, UserInfo } from "types/UserInfo";

export type GlobalState = {
  // loading will be true if any of API is being called. Should not be used to detect API completion of each API.
  loading: number;
  hardStop: any;
  // global error object.
  error: any;
  toast: ToastMessage | null;
  appIdle: boolean;
  sessionKey?: string;
  activationCode: string;
  userInfo: UserInfo;
  userDetail: UserDetails;
  token: string;
  globalLoading: boolean;
  featureFlags: Array<string>;
  tncForCidpUser: string | null;
  loadingCheckOnUpdatedTnC: boolean;
  loadingTnC: boolean;
  hasUserAcceptedTnC: boolean;
};

// The initial state of the App
export const initialState: GlobalState = {
  loading: 0,
  error: null,
  hardStop: null,
  toast: null,
  appIdle: false,
  sessionKey: "",
  activationCode: "",
  userInfo: {
    sub: "",
    cidpId: "",
    email: "",
    ext: {
      sub_type: "",
      auth_provider: "",
    },
    given_name: "",
    family_name: "",
    preferred_username: "",
  },
  userDetail: {
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    directLoginEnabled: false,
    identities: [
      {
        partnerId: "",
        partnerType: "",
        partnerName: "",
      },
    ],
    cidpId: "",
  },
  token: "",
  globalLoading: false,
  featureFlags: [],
  tncForCidpUser: null,
  loadingCheckOnUpdatedTnC: false,
  loadingTnC: false,
  hasUserAcceptedTnC: false,
};

// Pure Actions
export const sessionExpired = createAction("global/sessionExpired");
export const goToSecuritySettings = () => push("/dashboard/securitysettings");
export const getTnCForCidpUser = createAction<void>("global/getTnCForCidpUser");
export const setUserAcceptedNewTnC = createAction<void>(
  "global/setUserAcceptedNewTnC"
);

const slice = createSlice({
  name: "global",
  initialState,
  reducers: {
    startLoading(state) {
      state.loading += 1;
      state.error = null;
    },
    finishLoading(state) {
      state.loading = Math.max(0, state.loading - 1);
    },
    setError(state, action: PayloadAction<any>) {
      state.error = action.payload;
    },
    setAppIdle(state, action: PayloadAction<boolean>) {
      state.appIdle = action.payload;
    },
    sessionExtended(state) {
      state.appIdle = false;
    },
    showHideHardStop(state, action: PayloadAction<any>) {
      state.hardStop = action.payload;
    },
    setActivationCode(state, action: PayloadAction<string>) {
      state.activationCode = action.payload;
    },
    setGlobalUserInfo(state, action: PayloadAction<UserInfo>) {
      state.userInfo = action.payload;
    },
    clearGlobalUserInfo(state) {
      state.userInfo = {
        ...initialState.userInfo,
        ext: { ...initialState.userInfo.ext },
      };
    },
    setGlobalUserDetails(state, action: PayloadAction<UserDetails>) {
      state.userDetail = action.payload;
    },
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    setToast(state, action: PayloadAction<ToastMessage>) {
      state.toast = action.payload;
    },
    removeToken(state) {
      state.token = "";
    },
    setGlobalLoading(state, action: PayloadAction<boolean>) {
      state.globalLoading = action.payload;
    },
    setGlobalFeatureFlags(state, action: PayloadAction<Array<string>>) {
      state.featureFlags = action.payload;
    },
    afterTnCFetched(state: GlobalState, action: PayloadAction<string | null>) {
      state.loadingTnC = false;
      state.tncForCidpUser = action.payload;
    },
    afterFetchUserAcceptedTnC(
      state: GlobalState,
      action: PayloadAction<UpdatedTnCCheckResponse>
    ) {
      state.loadingCheckOnUpdatedTnC = false;
      state.hasUserAcceptedTnC = action.payload.message === "true";
    },
    afterUpdatedTnCAcceptance(
      state: GlobalState,
      action: PayloadAction<boolean>
    ) {
      state.loadingCheckOnUpdatedTnC = false;
      state.hasUserAcceptedTnC = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTnCForCidpUser, (state) => {
      state.loadingCheckOnUpdatedTnC = true;
      state.loadingTnC = true;
    });
    builder.addCase(setUserAcceptedNewTnC, (state) => {
      state.loadingCheckOnUpdatedTnC = true;
    });
  },
});

export const {
  startLoading,
  finishLoading,
  setError,
  setAppIdle,
  sessionExtended,
  showHideHardStop,
  setActivationCode,
  setGlobalUserInfo,
  clearGlobalUserInfo,
  setGlobalUserDetails,
  setToken,
  setToast,
  removeToken,
  setGlobalLoading,
  setGlobalFeatureFlags,
  afterFetchUserAcceptedTnC,
  afterTnCFetched,
  afterUpdatedTnCAcceptance,
} = slice.actions;

export default slice.reducer;
